import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type ChatState = {
  isChatLoading: boolean;
  isChatReady: boolean;
  isChatOpen: boolean;
  hasChatSessionStarted: boolean;
};

type ChatContextValue = ChatState & {
  openChat: () => void;
  closeChat: () => void;
  markChatReady: () => void;
  setChatLoading: (loading: boolean) => void;
  syncChatOpenState: (isOpen: boolean) => void;
};

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ChatState>({
    isChatLoading: false,
    isChatReady: false,
    isChatOpen: false,
    hasChatSessionStarted: false,
  });

  const openChat = useCallback(() => {
    setState((previous) => {
      const hasStarted = previous.hasChatSessionStarted;
      return {
        ...previous,
        isChatOpen: true,
        hasChatSessionStarted: true,
        isChatLoading: hasStarted ? previous.isChatLoading : true,
      };
    });
  }, []);

  const closeChat = useCallback(() => {
    setState((previous) => ({
      ...previous,
      isChatOpen: false,
    }));
  }, []);

  const markChatReady = useCallback(() => {
    setState((previous) => ({
      ...previous,
      isChatReady: true,
      isChatOpen: true,
      hasChatSessionStarted: true,
      isChatLoading: false,
    }));
  }, []);

  const setChatLoading = useCallback((loading: boolean) => {
    setState((previous) => ({
      ...previous,
      isChatLoading: loading,
    }));
  }, []);

  const syncChatOpenState = useCallback((isOpen: boolean) => {
    setState((previous) => ({
      ...previous,
      isChatOpen: isOpen,
      hasChatSessionStarted: previous.hasChatSessionStarted || isOpen,
    }));
  }, []);

  const value = useMemo<ChatContextValue>(
    () => ({
      ...state,
      openChat,
      closeChat,
      markChatReady,
      setChatLoading,
      syncChatOpenState,
    }),
    [state, openChat, closeChat, markChatReady, setChatLoading, syncChatOpenState]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChatContext(): ChatContextValue {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }

  return context;
}
